import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { useGetProductionTypesAsItems, useGetProfessionalAreasAsItems } from "modules/codebooks";
import {
    Button,
    ConfirmationModal,
    DatePicker,
    DisplayOrInput,
    Dropzone,
    Field,
    FingerprintDisplay,
    Input,
    Loading,
    Modal,
    ModalFooter,
    ModalHeader,
    RoleType,
    SearchableDropdown,
} from "modules/common";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RecordCategory, Record as RecordEntry } from "services/records";
import { RootState } from "store";
import {
    useBulkEditRecord,
    useCreateRecord,
    useDeleteRecord,
    useEditRecord,
    useGetRecord,
    useGetRecordCategoriesAsItems,
} from "..";
import { AccessibilityContext } from "views/AccessibilityContext";
import { classNames } from "helpers/ClassNameHelper";

interface Props {
    records?: RecordEntry[];
    recordId?: string | null;
    isOpen: boolean;
    handleClose: () => void;
    onPageChange?: (page: number) => void;
    chosenPage?: number;
    setCategory?: (categoryGuid: string) => void;
    isChecked?: boolean;
}

// TODO: use partial
const emptyRecord: RecordEntry = {
    guid: "",
    name: "",
    recordCategoryGuid: "",
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
    professionalAreaId: 0,
    professionalAreaGuid: "",
    productionTypeGuid: "",
    deadline: "",
    fileName: "",
    uploadedFile: null,
    categoryName: "",
};

type RecordProperty = keyof RecordEntry;

type RecordErrors = Partial<Record<RecordProperty, string>>;

export const RecordDetailsModal = ({
    records,
    recordId,
    isOpen,
    handleClose,
    chosenPage,
    onPageChange,
    setCategory,
    isChecked = false,
}: Props) => {
    const [record, setRecord] = useState<RecordEntry>(emptyRecord);
    const [errors, setErrors] = useState<RecordErrors>({});
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateRecord(record.uploadedFile);
    const edit = useEditRecord(record.uploadedFile);
    const bulkEdit = useBulkEditRecord(records?.map(record => record.guid) ?? []);
    const remove = useDeleteRecord();
    const [selectedProfessionalAreaGuid, setSelectedProfessionalAreaGuid] = useState<string>("");
    const { profile } = useSelector((state: RootState) => state.profile);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const professionalAreasAsItems = useGetProfessionalAreasAsItems();
    const productionTypesAsItemsFullList = useGetProductionTypesAsItems();
    const recordCategoriesAsItems = useGetRecordCategoriesAsItems();

    const {
        data: recordDetails,
        isFetching: isLoadingRecordDetails,
        refetch,
    } = useGetRecord(recordId!, Boolean(recordId));

    useEffect(() => {
        if (!isOpen || !recordId) return;
        refetch();
    }, [isOpen, recordId]);

    useEffect(() => {
        if (recordId && recordDetails) {
            setRecord(recordDetails);
            setCategory && setCategory(recordDetails.recordCategoryGuid);
        } else {
            setRecord(emptyRecord);
        }
    }, [recordDetails, recordId]);

    const productionTypesAsItems = useMemo(() => {
        if (!productionTypesAsItemsFullList) return [];

        if (record?.professionalAreaGuid) {
            const productionTypes = productionTypesAsItemsFullList?.filter(
                item => item.professionalAreaGuid === record.professionalAreaGuid
            );

            return (
                productionTypes?.map(item => ({
                    label: item.label,
                    id: item.id,
                    guid: item.guid,
                })) ?? []
            );
        } else if (selectedProfessionalAreaGuid) {
            const productionTypes = productionTypesAsItemsFullList?.filter(
                item => item.professionalAreaGuid === selectedProfessionalAreaGuid
            );

            return (
                productionTypes?.map(item => ({
                    label: item.label,
                    id: item.id,
                    guid: item.guid,
                })) ?? []
            );
        }
    }, [
        selectedProfessionalAreaGuid,
        record?.professionalAreaGuid,
        productionTypesAsItemsFullList,
    ]);

    const professionalArea = useMemo(() => {
        if (record?.professionalAreaGuid) {
            return professionalAreasAsItems?.find(
                item => item.guid === record?.professionalAreaGuid
            );
        }

        return professionalAreasAsItems?.find(item => item.id === record?.professionalAreaId);
    }, [professionalAreasAsItems, record?.professionalAreaGuid, record?.professionalAreaId]);

    const productionType = useMemo(
        () => productionTypesAsItems?.find(item => item.guid === record?.productionTypeGuid),
        [productionTypesAsItems, record?.productionTypeGuid]
    );

    const recordCategory = useMemo(
        () => recordCategoriesAsItems?.find(item => item.id === record?.recordCategoryGuid),
        [recordCategoriesAsItems, record.recordCategoryGuid]
    );

    // TODO: refactor to use error message object and validation object
    const validateRecord = (record: RecordEntry): RecordErrors => {
        const errors: RecordErrors = {};
        if (!isBulkEdit && !record.name.trim()) errors.name = "Naziv evidencije je obavezan";
        const hasProfessionalArea = record.professionalAreaGuid || record.professionalAreaId;
        if (!hasProfessionalArea) errors.professionalAreaGuid = "Područje je obavezno";
        if (
            !record.productionTypeGuid &&
            productionTypesAsItems &&
            productionTypesAsItems.length > 0
        )
            errors.productionTypeGuid = "Vrsta proizvodnje je obavezna";
        if (!record.deadline && !isBulkEdit) errors.deadline = "Rok ispunjenja je obavezan";
        if (!record.recordCategoryGuid) errors.recordCategoryGuid = "Kategorija je obavezna";
        if (!isBulkEdit) {
            const hasNewFile = record.uploadedFile != null;
            const hasExistingFile = record.fileName && record.fileName.trim() !== "";

            if (!hasNewFile && !hasExistingFile) {
                errors.uploadedFile = "Datoteka je obavezna";
            }
        }
        if (record.recordCategoryGuid === "new" && !record.categoryName.trim())
            errors.categoryName = "Naziv kategorije je obavezan";

        return errors;
    };

    useEffect(() => {
        if (!records) {
            return;
        }
        if (records.length === 1) {
            setRecord(records[0]);
        }
    }, [records, setRecord]);

    useEffect(() => {
        setErrors({});
        setIsEdit(records ? records.length > 1 : false);
        if (!isOpen) {
            setRecord(emptyRecord);
        }
    }, [isOpen, setErrors, setIsEdit, records]);

    useEffect(() => {
        if (isChecked && records && records.length === 1) {
            setRecord(records[0]);
        }
    }, [isChecked, records]);

    const isAdd = useMemo(() => {
        return !recordId && (!records || records.length === 0);
    }, [records, recordId]);

    const isBulkEdit = useMemo(() => {
        return records && records.length > 1;
    }, [records]);

    const title = useMemo(() => {
        if (isAdd) return "Nova evidencija";
        if (isEdit || isChecked) return "Uredi evidenciju";
        if (record) return record?.name || "Detalji evidencije";
        return "Detalji evidencije";
    }, [isAdd, isEdit, isChecked, record]);

    const changeRecordProperty = (
        property: RecordProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setRecord(prevState => ({ ...(prevState ?? emptyRecord), [property]: event.target.value }));
    };

    const changeRecordDropdownProperty = (
        property: RecordProperty,
        value: { value: string; label: string }
    ) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setRecord(prevState => ({ ...(prevState ?? emptyRecord), [property]: value.value }));
    };

    const changeRecordDateProperty = (property: RecordProperty, date: string | undefined) => {
        setErrors(prevState => ({ ...prevState, [property]: undefined }));
        setRecord(prevState => ({ ...(prevState ?? emptyRecord), [property]: date }));
    };

    const isValid = (property: RecordProperty) => {
        return !errors[property];
    };

    const getErrorMsg = (property: RecordProperty) => {
        return errors[property] ?? "";
    };

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        handleClose();
    };

    const onSave = async () => {
        const newErrors = validateRecord(record);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setIsButtonDisabled(true);
        if (isBulkEdit) {
            await bulkEdit.mutateAsync(record);
        } else if (isAdd) {
            await create.mutateAsync(record);
        } else {
            await edit.mutateAsync(record);
        }

        setErrors({});
        handleClose();
        setIsButtonDisabled(false);
    };

    const onDelete = async () => {
        if (!record) return;

        await remove.mutateAsync(record.guid);
        const remainingItems = (records?.length ?? 0) - 1;
        if (remainingItems <= 0 && (chosenPage ?? 1) > 1) onPageChange && onPageChange(1);
        setShowDelete(false);
        handleClose();
    };
    const { lowVision } = useContext(AccessibilityContext);
    return (
        <Modal
            isActive={isOpen}
            className="record-details-modal"
            onClose={onCancel}
            modalHeader={<ModalHeader onClose={onCancel} title={title} />}
            modalFooter={
                !record ? null : (
                    <ModalFooter>
                        {isAdd || isEdit || isChecked ? (
                            <>
                                {!isChecked && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        disabled={false}
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    disabled={isButtonDisabled}
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    disabled={profile?.roleType?.value !== RoleType.SuperAdmin}
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                            </>
                        )}
                    </ModalFooter>
                )
            }
        >
            <div className="record-details">
                {isLoadingRecordDetails ? (
                    <Loading />
                ) : (
                    <>
                        {!isBulkEdit && (
                            <div className="modal-section">
                                <Field
                                    name={
                                        isAdd
                                            ? "DODAJ DATOTEKU"
                                            : isEdit || isChecked
                                            ? "ZAMIJENI DATOTEKU"
                                            : "DATOTEKA"
                                    }
                                    errorMessage={getErrorMsg("uploadedFile")}
                                >
                                    {isAdd || isEdit || isChecked ? (
                                        <Dropzone
                                            handleDrop={(files: File[]) => {
                                                setRecord(prevState => ({
                                                    ...prevState,
                                                    uploadedFile: files[0],
                                                    fileName: files[0]?.name || prevState.fileName,
                                                }));
                                                setErrors(prevState => ({
                                                    ...prevState,
                                                    uploadedFile: undefined,
                                                }));
                                            }}
                                            handleDelete={_ => {
                                                setRecord(prevState => ({
                                                    ...prevState,
                                                    uploadedFile: null,
                                                }));
                                            }}
                                            files={record.uploadedFile ? [record.uploadedFile] : []}
                                        />
                                    ) : (
                                        <span
                                            className={classNames(
                                                "dropzone-file-name",
                                                lowVision && "low-vision"
                                            )}
                                        >
                                            {record?.fileName}
                                        </span>
                                    )}
                                </Field>
                            </div>
                        )}

                        <div className="modal-section">
                            <Field
                                name="STRUČNO PODRUČJE"
                                errorMessage={getErrorMsg("professionalAreaGuid")}
                            >
                                <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                    {professionalArea?.label}
                                    <SearchableDropdown
                                        items={professionalAreasAsItems}
                                        value={professionalArea}
                                        noOptionsMessage={() => "Nema pronađenih područja"}
                                        labelKey="label"
                                        onSelect={item => {
                                            setRecord(prevState => ({
                                                ...prevState,
                                                professionalAreaGuid: item?.guid ?? "",
                                                productionTypeGuid: "",
                                            }));
                                            setSelectedProfessionalAreaGuid(item?.guid ?? "");
                                        }}
                                        placeholder="Pretraživanje"
                                        customIcons={{
                                            searchIcon: <SearchIcon width={14} height={14} />,
                                        }}
                                        isValid={isValid("professionalAreaGuid")}
                                    />
                                </DisplayOrInput>
                            </Field>

                            <Field
                                name="VRSTA PROIZVODNJE"
                                errorMessage={getErrorMsg("productionTypeGuid")}
                            >
                                <DisplayOrInput
                                    key={record?.professionalAreaGuid}
                                    isEdit={isAdd || isEdit || isChecked}
                                >
                                    {productionType?.label}
                                    <SearchableDropdown
                                        items={productionTypesAsItems}
                                        value={productionType}
                                        noOptionsMessage={() => "Nema pronađenih vrsti proizvodnje"}
                                        labelKey="label"
                                        onSelect={item =>
                                            setRecord(prevState => ({
                                                ...prevState,
                                                productionTypeGuid: item?.guid ?? "",
                                            }))
                                        }
                                        placeholder="Pretraživanje"
                                        customIcons={{
                                            searchIcon: <SearchIcon width={14} height={14} />,
                                        }}
                                        isValid={isValid("productionTypeGuid")}
                                    />
                                </DisplayOrInput>
                            </Field>

                            {!isBulkEdit && (
                                <Field name="ROK ISPUNJENJA" errorMessage={getErrorMsg("deadline")}>
                                    <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                        {getDisplayDateFormat(record?.deadline)}
                                        <DatePicker
                                            value={
                                                record.deadline
                                                    ? new Date(record?.deadline)
                                                    : undefined
                                            }
                                            selectedDay={new Date()}
                                            onClick={date =>
                                                changeRecordDateProperty(
                                                    "deadline",
                                                    date?.toISOString()
                                                )
                                            }
                                            isValid={isValid("deadline")}
                                        />
                                    </DisplayOrInput>
                                </Field>
                            )}
                        </div>

                        <div className="modal-section">
                            <Field
                                name="KATEGORIJA"
                                errorMessage={getErrorMsg("recordCategoryGuid")}
                            >
                                <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                    {recordCategory?.label}
                                    <SearchableDropdown
                                        items={[{ label: "Nova kategorija", id: "new" }].concat(
                                            recordCategoriesAsItems ?? []
                                        )}
                                        value={recordCategory}
                                        noOptionsMessage={() => "Nema pronađenih kategorija"}
                                        labelKey="label"
                                        onSelect={item =>
                                            setRecord(prevState => ({
                                                ...prevState,
                                                recordCategoryGuid: item?.id ?? "",
                                            }))
                                        }
                                        placeholder="Pretraživanje"
                                        customIcons={{
                                            searchIcon: <SearchIcon width={14} height={14} />,
                                        }}
                                        isValid={isValid("recordCategoryGuid")}
                                        onChange={changeRecordDropdownProperty.bind(
                                            null,
                                            "recordCategoryGuid"
                                        )}
                                    />
                                </DisplayOrInput>
                            </Field>

                            {record.recordCategoryGuid === "new" && (
                                <Field
                                    name="NAZIV KATEGORIJE"
                                    errorMessage={getErrorMsg("categoryName")}
                                >
                                    <span className="modal-details-text">
                                        <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                            {record?.name}
                                            <Input
                                                id="categoryName"
                                                type="text"
                                                size="small"
                                                name="categoryName"
                                                isValid={isValid("categoryName")}
                                                value={record?.categoryName}
                                                onChange={changeRecordProperty.bind(
                                                    null,
                                                    "categoryName"
                                                )}
                                            />
                                        </DisplayOrInput>
                                    </span>
                                </Field>
                            )}
                        </div>

                        {!isBulkEdit && (
                            <div className="modal-section">
                                <Field name="NAZIV EVIDENCIJE" errorMessage={getErrorMsg("name")}>
                                    <span className="modal-details-text">
                                        <DisplayOrInput isEdit={isAdd || isEdit || isChecked}>
                                            {record?.name}
                                            <Input
                                                id="name"
                                                type="text"
                                                size="small"
                                                name="name"
                                                isValid={isValid("name")}
                                                value={record?.name}
                                                onChange={changeRecordProperty.bind(null, "name")}
                                            />
                                        </DisplayOrInput>
                                    </span>
                                </Field>
                            </div>
                        )}

                        {(record.created !== "" || record.lastUpdated !== "") &&
                            profile?.roleType?.value === RoleType.SuperAdmin && (
                                <>
                                    <div className="modal-section narrow">
                                        <FingerprintDisplay
                                            item={record}
                                            value="created"
                                            containerClassName="modal-section no-border"
                                            labelClassName="modal-details-label"
                                            textClassName="modal-details-text"
                                        />
                                    </div>
                                    <div className="modal-section narrow">
                                        <FingerprintDisplay
                                            item={record}
                                            value="lastUpdated"
                                            containerClassName="modal-section no-border"
                                            labelClassName="modal-details-label"
                                            textClassName="modal-details-text"
                                        />
                                    </div>
                                </>
                            )}
                    </>
                )}
            </div>

            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati evidenciju ${record?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
